import { parseIsoMs } from "@/features/opportunities/lib/format-relative-time";
import type {
  ScheduledJob,
  ScheduledJobRecentRun,
} from "@/features/scheduled-jobs/api/scheduled-jobs.api";
import type { EventJobStatus } from "@/features/events/api/events.api";

export type ScheduledJobRunOutcomeBucket = "completed" | "failed" | "inFlight";

export interface ScheduledJobRunPoint {
  /** Recent-run id — stable key for the bar and tooltip. */
  id: string;
  jobName: string;
  jobId: string;
  publishedAt: string;
  /** Epoch ms for the published stamp — drives day-span checks. */
  publishedMs: number | null;
  /** Short axis tick. */
  label: string;
  /** Full timestamp for the tooltip heading. */
  rangeLabel: string;
  /** Real duration when known; 0 when the run never recorded one. */
  durationMs: number;
  /**
   * Height the stem actually paints. Matches `durationMs` for timed runs; for
   * untimed ones it is a short stub so the outcome still shows on the axis
   * instead of disappearing into the baseline.
   */
  plotDurationMs: number;
  /** True when duration was missing — head is hollow, not a zero-second run. */
  isDurationUnknown: boolean;
  status: EventJobStatus;
  outcome: ScheduledJobRunOutcomeBucket;
  triggeredBySchedule: boolean;
  /** Non-null only for failed runs that carried a message. */
  errorMessage: string | null;
}

export interface ScheduledJobRunSeries {
  points: ScheduledJobRunPoint[];
  total: number;
  failed: number;
  completed: number;
  inFlight: number;
  /** Distinct jobs that contributed at least one stem. */
  jobsTouched: number;
  /** Stems fired by the cron itself (not Run now). */
  scheduledCount: number;
  /** Stems from an operator Run now. */
  manualCount: number;
  /** Mean of known durations — null when nothing finished with a timing. */
  avgDurationMs: number | null;
  /** p95 of known durations — null when fewer than one timed run. */
  p95DurationMs: number | null;
  /**
   * Run with the longest known duration. Null when every bar is a placeholder
   * (in-flight / untimed), so a "slowest" callout would be meaningless.
   */
  slowest: ScheduledJobRunPoint | null;
  /** True when any stem has a real duration to scale the Y axis against. */
  hasDuration: boolean;
  /** True when ticks need a date — clock time alone is ambiguous across days. */
  spansMultipleDays: boolean;
}

const EMPTY_SERIES: ScheduledJobRunSeries = {
  points: [],
  total: 0,
  failed: 0,
  completed: 0,
  inFlight: 0,
  jobsTouched: 0,
  scheduledCount: 0,
  manualCount: 0,
  avgDurationMs: null,
  p95DurationMs: null,
  slowest: null,
  hasDuration: false,
  spansMultipleDays: false,
};

/**
 * Caps the chart to the most recent runs so a long history does not crush the
 * axis into unreadability. Cloud Scheduler jobs are sparse; twenty stems still
 * leave room for a clock label without overlapping.
 */
export const SCHEDULED_JOB_RUN_CHART_LIMIT = 20;

/** Untimed stems paint at this fraction of the tallest timed run so they stay visible. */
const UNKNOWN_DURATION_STUB_RATIO = 0.08;
/** Floor when every run in the window is untimed — keeps the Y axis from collapsing. */
const UNKNOWN_DURATION_STUB_MS = 1_000;

export function outcomeFromStatus(status: EventJobStatus): ScheduledJobRunOutcomeBucket {
  if (status === "COMPLETED") return "completed";
  if (status === "FAILED") return "failed";
  return "inFlight";
}

/**
 * Flattens every job's recent runs into a chronological series the activity
 * chart can plot. Kept out of the component so outcome bucketing, averages,
 * and the slowest-run callout stay testable without mounting recharts.
 */
export function buildScheduledJobRunSeries(
  jobs: ScheduledJob[],
  locale?: string,
  limit: number = SCHEDULED_JOB_RUN_CHART_LIMIT,
): ScheduledJobRunSeries {
  if (jobs.length === 0) return EMPTY_SERIES;

  const flattened: Array<{ job: ScheduledJob; run: ScheduledJobRecentRun }> = [];
  for (const job of jobs) {
    for (const run of job.recentRuns) {
      flattened.push({ job, run });
    }
  }

  if (flattened.length === 0) return EMPTY_SERIES;

  flattened.sort((a, b) => {
    const aMs = parseIsoMs(a.run.publishedAt) ?? 0;
    const bMs = parseIsoMs(b.run.publishedAt) ?? 0;
    return aMs - bMs;
  });

  const windowed =
    flattened.length > limit ? flattened.slice(flattened.length - limit) : flattened;

  const publishedDays = new Set<string>();
  for (const { run } of windowed) {
    const ms = parseIsoMs(run.publishedAt);
    if (ms != null) publishedDays.add(dayKey(ms));
  }
  const spansMultipleDays = publishedDays.size > 1;

  const draft = windowed.map(({ job, run }) => {
    const publishedMs = parseIsoMs(run.publishedAt);
    const isDurationUnknown = run.runDurationMs == null;
    const outcome = outcomeFromStatus(run.status);
    const durationMs = run.runDurationMs ?? 0;

    return {
      id: run.id,
      jobName: job.name,
      jobId: job.id,
      publishedAt: run.publishedAt,
      publishedMs,
      label: formatAxisLabel(publishedMs, locale, spansMultipleDays),
      rangeLabel: formatRangeLabel(publishedMs, locale),
      durationMs,
      isDurationUnknown,
      status: run.status,
      outcome,
      triggeredBySchedule: run.triggeredBySchedule,
      errorMessage: run.errorMessage,
    };
  });

  let failed = 0;
  let completed = 0;
  let inFlight = 0;
  let scheduledCount = 0;
  let manualCount = 0;
  let durationSum = 0;
  let maxKnownDurationMs = 0;
  const knownDurations: number[] = [];
  const touchedJobIds = new Set<string>();
  let slowestDraft: (typeof draft)[number] | null = null;

  for (const point of draft) {
    touchedJobIds.add(point.jobId);
    if (point.outcome === "failed") failed += 1;
    else if (point.outcome === "completed") completed += 1;
    else inFlight += 1;

    if (point.triggeredBySchedule) scheduledCount += 1;
    else manualCount += 1;

    if (!point.isDurationUnknown) {
      durationSum += point.durationMs;
      knownDurations.push(point.durationMs);
      if (point.durationMs > maxKnownDurationMs) maxKnownDurationMs = point.durationMs;
      if (slowestDraft == null || point.durationMs > slowestDraft.durationMs) {
        slowestDraft = point;
      }
    }
  }

  const stubMs =
    maxKnownDurationMs > 0
      ? Math.max(1, Math.round(maxKnownDurationMs * UNKNOWN_DURATION_STUB_RATIO))
      : UNKNOWN_DURATION_STUB_MS;

  const points: ScheduledJobRunPoint[] = draft.map((point) => ({
    ...point,
    plotDurationMs: point.isDurationUnknown ? stubMs : point.durationMs,
  }));

  const slowest =
    slowestDraft == null
      ? null
      : (points.find((point) => point.id === slowestDraft.id) ?? null);

  return {
    points,
    total: points.length,
    failed,
    completed,
    inFlight,
    jobsTouched: touchedJobIds.size,
    scheduledCount,
    manualCount,
    avgDurationMs:
      knownDurations.length > 0 ? durationSum / knownDurations.length : null,
    p95DurationMs: percentileMs(knownDurations, 95),
    slowest,
    hasDuration: knownDurations.length > 0 || points.some((point) => point.isDurationUnknown),
    spansMultipleDays,
  };
}

/** Nearest-rank percentile over a copy so callers can pass unsorted samples. */
function percentileMs(samples: readonly number[], percentile: number): number | null {
  if (samples.length === 0) return null;
  const sorted = [...samples].sort((a, b) => a - b);
  const rank = Math.ceil((percentile / 100) * sorted.length) - 1;
  return sorted[Math.max(0, Math.min(sorted.length - 1, rank))] ?? null;
}

function dayKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function formatAxisLabel(ms: number | null, locale: string | undefined, includeDate: boolean): string {
  if (ms == null) return "—";
  const date = new Date(ms);
  const time = date.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (!includeDate) return time;
  const day = date.toLocaleDateString(locale, { month: "short", day: "numeric" });
  return `${day} ${time}`;
}

function formatRangeLabel(ms: number | null, locale?: string): string {
  if (ms == null) return "—";
  return new Date(ms).toLocaleString(locale, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}
